/**
 * TanStack Query hooks for purchasing. Keys are namespaced "purchasing/…";
 * cursor lists ride the shared ops-api helpers over the `{ items, next_cursor }`
 * envelope. Sort tokens are the contract's allowlisted `-field` spelling, so
 * every list fetcher serializes SortState through `toSortToken`.
 */
import { useQuery } from "@tanstack/react-query";
import {
  useCursorList,
  type CursorQueryResult,
  type ListFilters,
  type SortState,
} from "@/features/shared/ops-api";

import {
  GRN_SORTS,
  LANDED_COST_SORTS,
  PURCHASE_ORDER_SORTS,
  SUPPLIER_ITEM_SORTS,
  getApprovalPreview,
  getGrn,
  getLandedCost,
  getPurchaseOrder,
  getPurchasingConfiguration,
  listBatches,
  listGrns,
  listLandedCosts,
  listPurchaseOrders,
  listReorderSuggestions,
  listSupplierItems,
  listSupplierParties,
  listTransmissions,
  listUoms,
  listVariantBarcodes,
  listVariants,
  listWarehouseLocations,
  listWarehouses,
  toSortToken,
  type Batch,
  type GrnSummary,
  type LandedCostStatus,
  type LandedCostDocumentSummary,
  type PartySummary,
  type PurchaseOrderStatus,
  type PurchaseOrderSummary,
  type PurchasingConfiguration,
  type ReorderSuggestion,
  type ReorderSuggestionStatus,
  type SupplierItem,
  type Transmission,
  type Uom,
  type Variant,
  type Warehouse,
  type WarehouseLocation,
} from "./api";

export const purchasingKeys = {
  purchaseOrders: {
    all: ["purchasing/purchase-orders"] as const,
    list: (filters: Record<string, unknown>) =>
      ["purchasing/purchase-orders", "list", filters] as const,
    detail: (id: string) => ["purchasing/purchase-orders", "detail", id] as const,
    approvalPreview: (id: string) =>
      ["purchasing/purchase-orders", "approval-preview", id] as const,
    transmissions: (id: string) => ["purchasing/purchase-orders", "transmissions", id] as const,
  },
  grns: {
    all: ["purchasing/grns"] as const,
    list: (filters: Record<string, unknown>) => ["purchasing/grns", "list", filters] as const,
    detail: (id: string) => ["purchasing/grns", "detail", id] as const,
  },
  landedCosts: {
    all: ["purchasing/landed-costs"] as const,
    list: (filters: Record<string, unknown>) =>
      ["purchasing/landed-costs", "list", filters] as const,
    detail: (id: string) => ["purchasing/landed-costs", "detail", id] as const,
  },
  supplierItems: {
    all: ["purchasing/supplier-items"] as const,
    list: (filters: Record<string, unknown>) =>
      ["purchasing/supplier-items", "list", filters] as const,
    detail: (id: string) => ["purchasing/supplier-items", "detail", id] as const,
  },
  reorderSuggestions: {
    all: ["purchasing/reorder-suggestions"] as const,
    list: (filters: Record<string, unknown>) =>
      ["purchasing/reorder-suggestions", "list", filters] as const,
  },
  config: { current: () => ["purchasing/config", "current"] as const },
  lookups: {
    suppliers: (q: string) => ["purchasing/lookups", "suppliers", q] as const,
    variants: (q: string) => ["purchasing/lookups", "variants", q] as const,
    uoms: () => ["purchasing/lookups", "uoms"] as const,
    warehouses: () => ["purchasing/lookups", "warehouses"] as const,
    locations: (warehouseId: string) =>
      ["purchasing/lookups", "warehouse-locations", warehouseId] as const,
    batches: (variantId: string, warehouseId: string) =>
      ["purchasing/lookups", "batches", variantId, warehouseId] as const,
    barcodeIndex: (variantIds: readonly string[]) =>
      ["purchasing/lookups", "barcode-index", variantIds] as const,
  },
};

// ── Purchase orders ──────────────────────────────────────────────────────

export interface PurchaseOrderFilters extends ListFilters {
  status?: PurchaseOrderStatus;
  supplier_id?: string;
  expected_from?: string;
  expected_to?: string;
  q?: string;
}

export function usePurchaseOrderList(
  filters: PurchaseOrderFilters,
  sort: SortState,
  limit?: number,
): CursorQueryResult<PurchaseOrderSummary> {
  return useCursorList(
    purchasingKeys.purchaseOrders.list({ ...filters, sort: sort?.key, dir: sort?.dir, limit }),
    ({ cursor, limit: pageLimit, sort: pageSort, signal }) =>
      listPurchaseOrders(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
          ...(() => {
            const token = toSortToken(pageSort, PURCHASE_ORDER_SORTS);
            return token === undefined ? {} : { sort: token };
          })(),
        },
        signal,
      ),
    { sort, limit },
  );
}

/** PO body + ETag snapshot; the ETag validates every subsequent PO write. */
export type PurchaseOrderSnapshot = Awaited<ReturnType<typeof getPurchaseOrder>>;

export function usePurchaseOrderDetail(purchaseOrderId: string | undefined) {
  return useQuery({
    queryKey: purchasingKeys.purchaseOrders.detail(purchaseOrderId ?? "new"),
    queryFn: (): Promise<PurchaseOrderSnapshot> => getPurchaseOrder(purchaseOrderId as string),
    enabled: purchaseOrderId !== undefined,
  });
}

/** Pre-submit policy evaluation — read-only, creates no workflow task. */
export function useApprovalPreview(purchaseOrderId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: purchasingKeys.purchaseOrders.approvalPreview(purchaseOrderId ?? ""),
    queryFn: () => getApprovalPreview(purchaseOrderId as string),
    enabled: purchaseOrderId !== undefined && enabled,
    retry: false,
  });
}

export function useTransmissions(
  purchaseOrderId: string | undefined,
  enabled = true,
): CursorQueryResult<Transmission> {
  return useCursorList(
    purchasingKeys.purchaseOrders.transmissions(purchaseOrderId ?? ""),
    ({ cursor, limit, signal }) =>
      listTransmissions(
        purchaseOrderId as string,
        {
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(limit === undefined ? {} : { limit }),
        },
        signal,
      ),
    { enabled: purchaseOrderId !== undefined && enabled },
  );
}

// ── Goods receipts ───────────────────────────────────────────────────────

export interface GrnFilters extends ListFilters {
  supplier_id?: string;
  purchase_order_id?: string;
  warehouse_id?: string;
  received_from?: string;
  received_to?: string;
}

export function useGrnList(
  filters: GrnFilters,
  sort: SortState,
  limit?: number,
): CursorQueryResult<GrnSummary> {
  return useCursorList(
    purchasingKeys.grns.list({ ...filters, sort: sort?.key, dir: sort?.dir, limit }),
    ({ cursor, limit: pageLimit, sort: pageSort, signal }) =>
      listGrns(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
          ...(() => {
            const token = toSortToken(pageSort, GRN_SORTS);
            return token === undefined ? {} : { sort: token };
          })(),
        },
        signal,
      ),
    { sort, limit },
  );
}

export function useGrnDetail(grnId: string | undefined) {
  return useQuery({
    queryKey: purchasingKeys.grns.detail(grnId ?? ""),
    queryFn: () => getGrn(grnId as string),
    enabled: grnId !== undefined,
  });
}

// ── Landed costs ─────────────────────────────────────────────────────────

export interface LandedCostFilters extends ListFilters {
  status?: LandedCostStatus;
  supplier_id?: string;
  created_from?: string;
  created_to?: string;
}

export function useLandedCostList(
  filters: LandedCostFilters,
  sort: SortState,
  limit?: number,
): CursorQueryResult<LandedCostDocumentSummary> {
  return useCursorList(
    purchasingKeys.landedCosts.list({ ...filters, sort: sort?.key, dir: sort?.dir, limit }),
    ({ cursor, limit: pageLimit, sort: pageSort, signal }) =>
      listLandedCosts(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
          ...(() => {
            const token = toSortToken(pageSort, LANDED_COST_SORTS);
            return token === undefined ? {} : { sort: token };
          })(),
        },
        signal,
      ),
    { sort, limit },
  );
}

export type LandedCostSnapshot = Awaited<ReturnType<typeof getLandedCost>>;

export function useLandedCostDetail(landedCostId: string | undefined) {
  return useQuery({
    queryKey: purchasingKeys.landedCosts.detail(landedCostId ?? "new"),
    queryFn: (): Promise<LandedCostSnapshot> => getLandedCost(landedCostId as string),
    enabled: landedCostId !== undefined,
  });
}

// ── Supplier items ───────────────────────────────────────────────────────

export interface SupplierItemFilters extends ListFilters {
  supplier_id?: string;
  variant_id?: string;
  q?: string;
  preferred?: boolean;
  active?: boolean;
}

export function useSupplierItemList(
  filters: SupplierItemFilters,
  sort: SortState,
  limit?: number,
): CursorQueryResult<SupplierItem> {
  return useCursorList(
    purchasingKeys.supplierItems.list({ ...filters, sort: sort?.key, dir: sort?.dir, limit }),
    ({ cursor, limit: pageLimit, sort: pageSort, signal }) =>
      listSupplierItems(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
          ...(() => {
            const token = toSortToken(pageSort, SUPPLIER_ITEM_SORTS);
            return token === undefined ? {} : { sort: token };
          })(),
        },
        signal,
      ),
    { sort, limit },
  );
}

/**
 * Supplier-item defaults for one supplier, keyed by variant — the PO editor's
 * defaulting source (unit cost, MOQ, pack size, lead time, supplier SKU).
 */
export function useSupplierItemsBySupplier(supplierId: string | undefined, enabled = true) {
  return useQuery<SupplierItem[]>({
    queryKey: purchasingKeys.supplierItems.list({ supplier_id: supplierId ?? "", all: true }),
    queryFn: async ({ signal }) => {
      const page = await listSupplierItems(
        { supplier_id: supplierId as string, active: true, limit: 200 },
        signal,
      );
      return page.items;
    },
    enabled: supplierId !== undefined && supplierId !== "" && enabled,
  });
}

// ── Reorder suggestions ──────────────────────────────────────────────────

export interface ReorderSuggestionFilters extends ListFilters {
  status?: ReorderSuggestionStatus;
  supplier_id?: string;
  warehouse_id?: string;
  variant_id?: string;
}

export function useReorderSuggestionList(
  filters: ReorderSuggestionFilters,
  limit?: number,
): CursorQueryResult<ReorderSuggestion> {
  return useCursorList(
    purchasingKeys.reorderSuggestions.list({ ...filters, limit }),
    ({ cursor, limit: pageLimit, signal }) =>
      listReorderSuggestions(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
        },
        signal,
      ),
    { limit },
  );
}

// ── Configuration + lookups ──────────────────────────────────────────────

/** Tolerances the screens warn against before the server refuses. */
export function usePurchasingConfig(enabled = true) {
  return useQuery<PurchasingConfiguration>({
    queryKey: purchasingKeys.config.current(),
    queryFn: ({ signal }) => getPurchasingConfiguration(signal),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useSupplierOptions(q = "", enabled = true) {
  return useQuery<PartySummary[]>({
    queryKey: purchasingKeys.lookups.suppliers(q),
    queryFn: ({ signal }) => listSupplierParties(q === "" ? undefined : q, signal),
    enabled,
  });
}

export function useVariantOptions(q = "", enabled = true) {
  return useQuery<Variant[]>({
    queryKey: purchasingKeys.lookups.variants(q),
    queryFn: ({ signal }) => listVariants(q === "" ? undefined : q, signal),
    enabled,
  });
}

export function useUomOptions(enabled = true) {
  return useQuery<Uom[]>({
    queryKey: purchasingKeys.lookups.uoms(),
    queryFn: ({ signal }) => listUoms(signal),
    enabled,
  });
}

export function useWarehouseOptions(enabled = true) {
  return useQuery<Warehouse[]>({
    queryKey: purchasingKeys.lookups.warehouses(),
    queryFn: ({ signal }) => listWarehouses(signal),
    enabled,
  });
}

export function useWarehouseLocationOptions(warehouseId: string | undefined, enabled = true) {
  return useQuery<WarehouseLocation[]>({
    queryKey: purchasingKeys.lookups.locations(warehouseId ?? ""),
    queryFn: ({ signal }) => listWarehouseLocations(warehouseId as string, signal),
    enabled: warehouseId !== undefined && warehouseId !== "" && enabled,
  });
}

/** Distinct variants a receiving screen indexes barcodes for in one pass. */
const MAX_INDEXED_VARIANTS = 50;

/**
 * barcode → variant id for the variants on one purchase order.
 *
 * The contract publishes `GET /variants?barcode=`, but the deployed catalog
 * service refuses that parameter, and a floor screen cannot depend on a
 * round-trip that 400s. Indexing the order's own variants up front also makes
 * every scan resolve instantly and offline-ish; the server filter is still
 * tried for a barcode that is NOT on this order, so "unknown barcode" and
 * "not on this purchase order" stay distinguishable.
 */
export function useBarcodeIndex(variantIds: readonly string[], enabled = true) {
  const ids = [...new Set(variantIds)].slice(0, MAX_INDEXED_VARIANTS);
  return useQuery<Record<string, string>>({
    queryKey: purchasingKeys.lookups.barcodeIndex(ids),
    queryFn: async ({ signal }) => {
      const index: Record<string, string> = {};
      const pages = await Promise.all(
        ids.map(async (variantId) => ({
          variantId,
          barcodes: await listVariantBarcodes(variantId, signal),
        })),
      );
      for (const page of pages) {
        for (const barcode of page.barcodes) index[barcode.barcode] = page.variantId;
      }
      return index;
    },
    enabled: enabled && ids.length > 0,
    staleTime: 5 * 60_000,
  });
}

export function useBatchOptions(
  variantId: string | undefined,
  warehouseId: string | undefined,
  enabled = true,
) {
  return useQuery<Batch[]>({
    queryKey: purchasingKeys.lookups.batches(variantId ?? "", warehouseId ?? ""),
    queryFn: ({ signal }) => listBatches(variantId as string, warehouseId, signal),
    enabled: variantId !== undefined && variantId !== "" && enabled,
  });
}
